// "What needs you" for the Calm home (V4) and the alert box (2026-10-04, UX review): one ranked list, each fact once.
// Sources: live health issues, MAIN positions without a stop-loss (the live broker check is the authority), a Schwab
// login that is close, and the system_alerts rows grouped by type. The alert rows that only restate the unprotected
// position are dropped while the live check is loaded (best practices 3.17 still holds: the live row names the symbols).
// Pure functions, tested by frontend/tests/attention.test.mjs.
import { groupAlerts } from './alertGroups.js'
import { healthIssues } from './health.js'

const STOP_ALERT = /protective stop|no stop/i
const SCHWAB_WARN_HOURS = 48

export function isStopAlert(title) {
  return STOP_ALERT.test(String(title || ''))
}

// Returns {status: 'critical'|'attention'|'ok', items: [{key, sev, rank, title, why, href?, action?, ackIds?, count?, latest?}]}
export function buildAttention({ health, ws, alerts, mainSummary, schwab, now = new Date() } = {}) {
  const items = []
  for (const i of healthIssues({ health, ws, now })) {
    items.push({ key: i.key, sev: i.sev, rank: i.sev === 'critical' ? 0 : 3, title: i.title, why: i.why })
  }
  if (mainSummary && mainSummary.unprotected_count > 0) {
    const n = mainSummary.unprotected_count
    const list = mainSummary.unprotected
    items.push({
      key: 'unprotected', sev: 'critical', rank: 1,
      title: `${n} MAIN position${n === 1 ? '' : 's'} without a stop-loss`,
      why: `${list.slice(0, 4).join(', ')}${n > 4 ? ` and ${n - 4} more` : ''} can lose more than planned until a stop-loss is set.`,
      href: `/positions?s=${list[0]}`, action: 'View position',
    })
  }
  const deadline = schwab && schwab.refresh_token_reauth_deadline
  if (deadline) {
    const hours = (new Date(deadline).getTime() - now.getTime()) / 3600000
    if (!Number.isNaN(hours) && hours < SCHWAB_WARN_HOURS) {
      items.push({
        key: 'schwab', sev: hours <= 0 ? 'critical' : 'warning', rank: hours <= 0 ? 0 : 2,
        title: hours <= 0 ? 'Schwab login has expired' : 'Schwab login needed soon',
        why: hours <= 0 ? 'Market data will stop until you log in again.' : `Log in again before ${new Date(deadline).toLocaleString()}.`,
        href: '/settings', action: 'Open Settings',
      })
    }
  }
  const stopFactLoaded = Boolean(mainSummary)
  const rows = (alerts || []).filter(a => !(stopFactLoaded && mainSummary.unprotected_count > 0 && isStopAlert(a.title)))
  for (const g of groupAlerts(rows)) {
    const stale = stopFactLoaded && mainSummary.unprotected_count === 0 && isStopAlert(g.sample)
    items.push({
      key: `alert:${g.key}`, sev: g.severity, rank: g.severity === 'critical' ? 0 : 4,
      title: stale ? `${g.sample} (no longer true)` : g.sample, count: g.count, latest: g.latest, ackIds: g.ids,
      why: stale ? 'The live check finds every position protected, so this alert is out of date. Dismiss it.' : `${g.source}${g.subjects.length > 1 ? ` · ${g.subjects.join(', ')}` : ''}`,
    })
  }
  items.sort((a, b) => a.rank - b.rank || (b.count || 0) - (a.count || 0))
  const status = items.some(i => i.sev === 'critical') ? 'critical' : items.length ? 'attention' : 'ok'
  return { status, items }
}

export function statusSentence({ status, items }) {
  if (status === 'ok') return 'All good'
  if (status === 'critical') return items.length === 1 ? '1 thing needs you now' : `${items.length} things need you, one is urgent`
  return items.length === 1 ? '1 thing to review' : `${items.length} things to review`
}
